/**
 * ISLAND GEO — Balearic island detection + island-aware storefront text.
 *
 * Probe (measurement): once per session, asks the collector Worker to
 * geolocate and classify the visitor, and logs one console line. The Worker
 * owns the classification and records the result server-side on that same
 * call, so the client ships no place lists and makes no second request.
 *
 * Swap (personalisation): visitors resolved to Menorca or Ibiza see the word
 * "Mallorca" replaced by their island in exactly two scopes — the slideshow
 * hero title and the announcement marquee messages. Everyone else
 * (mallorca / unknown_es / foreign / no data) sees the server-rendered
 * default untouched; crawlers and SEO always get "Mallorca".
 *
 * DOM writes are confined to text nodes inside the two swap scopes below.
 * Every failure path is silent: a broken script must never break the
 * storefront.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mc_geo_probe';
  var ISLAND_URL = 'https://miele-geo-probe.noel-bb3.workers.dev/island';
  var TIMEOUT_MS = 3000;

  // Swap targets — class and element names come from the sections' own
  // markup, not from position:
  // - sections/slideshow.liquid wraps slides in <slideshow-component> and
  //   renders titles as h2.banner__heading; scoping through the component
  //   excludes the same heading class in image-banner/email-signup-banner.
  // - Both announcement marquee sections (texto-deslizante and
  //   barra-anuncio-deslizante) declare "section-marquee" as their Shopify
  //   section wrapper class and render every message as .marquee-item.
  var HERO_TITLE_SELECTOR = 'slideshow-component .banner__heading';
  var MARQUEE_SECTION_SELECTOR = '.section-marquee';
  var MARQUEE_ITEM_SELECTOR = '.marquee-item';

  var DISPLAY_NAMES = { menorca: 'Menorca', ibiza: 'Ibiza' };

  // Standalone word only, case-aware: "Mallorca" -> "Menorca"/"Ibiza" and
  // "MALLORCA" -> "MENORCA"/"IBIZA". Anything else — "Mallorquina",
  // lowercase "mallorca" — is left alone.
  var MALLORCA_WORD = /\b(MALLORCA|Mallorca)\b/g;

  /**
   * Shopify injects the Customer Privacy API at runtime when a consent banner
   * is configured, so its presence cannot be determined from theme source.
   * Feature-detect instead: honour consent when the API is there, run
   * unimpeded when it is not.
   */
  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') {
        return true;
      }
      return privacy.analyticsProcessingAllowed() === true;
    } catch (error) {
      return false; // API present but unhealthy: do not measure
    }
  }

  function swapText(value, display) {
    return value.replace(MALLORCA_WORD, function (match) {
      return match === 'MALLORCA' ? display.toUpperCase() : display;
    });
  }

  // Rewrites text nodes only. Attributes and element structure are never
  // touched, so the swap cannot break markup, listeners or styling.
  function swapTextNodes(root, display) {
    var children = root.childNodes;
    for (var i = 0; i < children.length; i++) {
      var node = children[i];
      if (node.nodeType === 3) {
        var swapped = swapText(node.data, display);
        if (swapped !== node.data) node.data = swapped;
      } else if (node.nodeType === 1) {
        swapTextNodes(node, display);
      }
    }
  }

  function swapMarqueeSection(section, display) {
    var items = section.querySelectorAll(MARQUEE_ITEM_SELECTOR);
    for (var i = 0; i < items.length; i++) {
      swapTextNodes(items[i], display);
    }
  }

  function swapAll(display) {
    var heroTitles = document.querySelectorAll(HERO_TITLE_SELECTOR);
    for (var i = 0; i < heroTitles.length; i++) {
      swapTextNodes(heroTitles[i], display);
    }

    var sections = document.querySelectorAll(MARQUEE_SECTION_SELECTOR);
    for (var j = 0; j < sections.length; j++) {
      swapMarqueeSection(sections[j], display);
    }
  }

  /**
   * The marquee's movement is a pure CSS transform animation — the theme
   * never re-renders its DOM on a timer. What can replace the DOM wholesale
   * is the theme editor and section-rendering apps, which would silently
   * undo the swap. Observe each marquee section — never the whole
   * document — and re-apply. Writes only happen when "Mallorca" is present,
   * so the observer cannot feed itself.
   */
  function watchMarqueeSections(display) {
    if (typeof MutationObserver !== 'function') return;
    var sections = document.querySelectorAll(MARQUEE_SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) {
      (function (section) {
        var observer = new MutationObserver(function () {
          swapMarqueeSection(section, display);
        });
        observer.observe(section, {
          childList: true,
          subtree: true,
          characterData: true
        });
      })(sections[i]);
    }
  }

  // Observers are created only on the menorca/ibiza path, so for every other
  // label none exist to disconnect and the DOM is provably untouched.
  function runSwap(label) {
    var display = DISPLAY_NAMES[label];
    if (!display) return;
    try {
      swapAll(display);
      watchMarqueeSections(display);
    } catch (error) {
      /* silent */
    }
  }

  function probe() {
    var shop = (window.Shopify && window.Shopify.shop) || '';
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, TIMEOUT_MS);

    fetch(ISLAND_URL + '?shop=' + encodeURIComponent(shop), {
      signal: controller.signal,
      mode: 'cors',
      credentials: 'omit'
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        // No island means the Worker could not classify this visitor. Treat
        // that as a failure rather than caching an absent label: a bad value
        // in sessionStorage would persist for the whole session.
        if (!data || !data.island) return;

        var island = data.island;
        var city = data.city || '';
        var region = data.region || '';

        console.log(
          '[geo-probe] city=' + city + ' region=' + region + ' island=' + island + ' source=worker'
        );

        try {
          sessionStorage.setItem(STORAGE_KEY, island);
        } catch (error) {
          /* silent */
        }

        runSwap(island);
      })
      .catch(function () {})
      .then(function () {
        clearTimeout(timer);
      });
  }

  function start() {
    try {
      var stored = null;
      var storageOk = true;
      try {
        stored = sessionStorage.getItem(STORAGE_KEY);
      } catch (error) {
        storageOk = false;
      }

      // A label resolved earlier in the session swaps instantly, no network.
      // Consent gates the Worker lookup, not reading back what this same
      // session already established.
      if (stored !== null) {
        runSwap(stored);
        return;
      }

      if (!storageOk) return; // once-per-session cannot be honoured: no probe
      if (!analyticsAllowed()) return;

      probe();
    } catch (error) {
      /* silent */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
